import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Onboarding } from '@ofself/shell';
import AccountFrame from '../components/AccountFrame';
import { useToast } from '../components/common';
import api from '../services/api';
import {
  deriveEscrowKey,
  unwrapKeyFromEscrow,
  deriveWrappingKey,
  wrapPrivateKey,
} from '../utils/fieldEncryption';

function ForgotPassword() {
  const [identifier, setIdentifier] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const result = await api.recoveryResetPassword(identifier.trim(), recoveryCode.trim(), newPassword, confirmPassword);

      // Attempt key re-wrap so encrypted data remains accessible after the reset
      const { enc_key_escrow, user_id, enc_public_key, access_token } = result || {};
      if (enc_key_escrow && user_id) {
        try {
          api.setToken(access_token);
          const escrowKey = await deriveEscrowKey(recoveryCode.trim(), user_id);
          const privateKey = await unwrapKeyFromEscrow(enc_key_escrow, escrowKey);
          const newWrappingKey = await deriveWrappingKey(newPassword, user_id);
          const newWrapped = await wrapPrivateKey(privateKey, newWrappingKey);
          await api.request('/auth/enc-keys', {
            method: 'PUT',
            body: JSON.stringify({ enc_public_key, enc_private_key_wrapped: newWrapped }),
          });
          api.setToken(null); // Clear temporary token; user must log in fresh
          toast.success('Password reset successful. Your encrypted data has been recovered. Please sign in.');
        } catch {
          api.setToken(null);
          toast.warning('Password reset successful, but encrypted data could not be re-keyed. Please sign in.');
        }
      } else {
        toast.success('Password reset successful. Please sign in.');
      }

      navigate('/login');
    } catch (err) {
      toast.error(err.message || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  // The account frame (components/AccountFrame): the shell's <Onboarding>
  // surface. The app owns state and routing.
  return (
    <AccountFrame>
      <Onboarding.Step
        // the fields say what the page is; the title is for a reader alone
        title="Reset your password"
        titleHidden
        note={(
          <a href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>Remembered it? Back to sign in</a>
        )}
        centered
      >
        <form className="account-form" onSubmit={handleSubmit}>
          <Onboarding.Fields>
            <Onboarding.Field
              label="email or username"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              autoFocus
              required
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
            />
            <Onboarding.Field
              label="recovery code"
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              required
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
            />
            <Onboarding.Field
              label="new password"
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <Onboarding.Field
              label="new password, again"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </Onboarding.Fields>
          <Onboarding.Next type="submit" disabled={loading} go={!loading}>
            {loading ? 'Resetting…' : 'Reset password'}
          </Onboarding.Next>
        </form>
      </Onboarding.Step>
    </AccountFrame>
  );
}

export default ForgotPassword;
